import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ModernHeader } from './ModernHeader';
import { ModernFooter } from './ModernFooter';
import A11yToolbar from '@/components/accessibility/A11yToolbar';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { isLyrraOne } from '@/lib/platform';
import { usePublicRegionStore } from '@/stores/public-region.store';

const SKIP_LINK_LABELS: Record<string, string> = {
  fr: 'Aller au contenu principal',
  en: 'Skip to main content',
  es: 'Ir al contenido principal',
  it: 'Vai al contenuto principale',
  de: 'Zum Hauptinhalt springen',
  pt: 'Saltar para o conteúdo principal',
  ar: 'انتقل إلى المحتوى الرئيسي',
  ru: 'Перейти к основному содержимому',
  uk: 'Перейти до основного вмісту',
  lb: 'Zum Haaptinhalt sprangen',
  tr: 'Ana içeriğe geç',
  zgh: 'Aller au contenu principal',
};

const LYRRAONE_AUTH_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password']);

export function PublicLayout() {
  const location = useLocation();
  const { language } = useTranslation();
  const skipLabel = SKIP_LINK_LABELS[language] ?? SKIP_LINK_LABELS.en;

  useEffect(() => {
    if (!isLyrraOne()) return;
    usePublicRegionStore.getState().applyFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    const rawPath = `${location.pathname}${location.search || ''}`;
    const path = rawPath.slice(0, 512);
    const href = window.location.href;
    const url = href.length <= 2048 ? href : undefined;
    const referrerRaw = document.referrer?.trim();
    const referrer = referrerRaw ? referrerRaw.slice(0, 2048) : undefined;
    void api
      .post('/public/telemetry/public-page-visit', {
        path: path || '/',
        ...(url ? { url } : {}),
        ...(referrer ? { referrer } : {}),
      })
      .catch(() => undefined);
  }, [location.pathname, location.search]);

  const isLyrraOneAuthPage = isLyrraOne() && LYRRAONE_AUTH_PATHS.has(location.pathname);

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-slate-900 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-white"
      >
        {skipLabel}
      </a>
      {!isLyrraOneAuthPage && <ModernHeader />}
      <div className={cn(
        'flex min-h-0 flex-1 flex-col',
        !isLyrraOneAuthPage && 'public-readable',
        !isLyrraOneAuthPage && isLyrraOne() && 'bg-[#FFFFFF]',
        isLyrraOneAuthPage && 'bg-[#F8FAFC]',
      )}>
        <main id="main-content" className="flex min-h-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>
      {!isLyrraOneAuthPage && <ModernFooter />}
      <A11yToolbar fabPosition="left" panelPosition="left" />
    </div>
  );
}
